<script lang="ts" setup>
import { ref, computed } from 'vue';
import { $fetch } from '@/home/plugins/fetch';
import Pagination from '@/home/components/Pagination.vue';
import { useAppStore } from '@/home/stores/app';
import { useAsyncData } from '@/home/hooks/async-data';
import { statsPv } from '@/home/utils';

const props = defineProps({
  categoryId: {
    type: String,
    default: '',
  },

  offset: {
    type: String,
    default: '',
  },
});

const appStore = useAppStore();

const pathPrefix = '/journalism/index/';
const defaultTitle = '新闻动态';
const defaultCategoryId = 'all';
const limit = 12;

const pager = ref({});

const getTitle = () => {
  if (!props.categoryId || props.categoryId === defaultCategoryId) {
    return defaultTitle;
  }

  const category = categories.value?.find((item: any) => item.categoryId === props.categoryId);

  return category?.categoryName ? `${category.categoryName} - ${defaultTitle}` : defaultTitle;
}

const [{ data: categoryData }, { data: listing }] = useAsyncData<any[] & Record<string, any>>(
  {
    key: 'category',
    handler: () => $fetch('/news/v1/category'),
  },
  {
    key: 'journalism',
    handler: () => $fetch('/news/v2/page', {
      data: {
        cids: props.categoryId === defaultCategoryId || !props.categoryId ? [] : [props.categoryId],
        excludeCids: import.meta.env.VITE_LAW_TRANSLATOR_CATEGORY_ID ? [import.meta.env.VITE_LAW_TRANSLATOR_CATEGORY_ID] : [],
        offset: Number(props.offset || 0),
        limit,
      },
    }),
    options: {
      cache: false,
      watch: [() => props.categoryId, () => props.offset],
      onAfterHandle({ handleSuccess, serverContext }) {
        if (!handleSuccess) {
          return;
        }

        pager.value = {
          offset: Number(props.offset || 0),
          total: listing.value?.totalCount ?? 0,
        };

        if (serverContext) {
          serverContext.title = getTitle();
        }

        if (!import.meta.env.SSR) {
          appStore.setTitle(getTitle());

          // 统计埋点
          statsPv();
        }
      },
    },
  },
);

const categories = computed(() => {
  const result = [{
    categoryId: defaultCategoryId,
    categoryName: '全部',
  }];

  if (categoryData.value) {
    result.push(...categoryData.value);
  }

  return result;
});

const selectedIndex = computed(() => {
  const index = categories.value?.findIndex((item: any) => item.categoryId === props.categoryId) ?? 0;

  return index < 0 ? 0 : index;
});

const setRouterPath = (offset: number) => `${pathPrefix}${props.categoryId || defaultCategoryId}/${offset}`;
</script>

<template>
  <main class="page-journalism pb-160px color-hex-2d2d2d">
    <section class="journalism-title h-400px bg-cover bg-center bg-no-repeat">
      <div class="journalism-title__container mx-auto w-1360px pt-143px <2xl:w-1200px">
        <h1 class="m-0 text-50px font-medium leading-70px">新闻动态</h1>
        <!-- <p class="m-0 mt-8px text-24px leading-36px">
          新闻动态的描述
        </p> -->
      </div>
    </section>

    <section class="mx-auto min-h-900px w-1360px <2xl:w-1200px">
      <div class="relative mt-48px w-full flex justify-between pb-18px" v-if="categories">
        <RouterLink
          v-for="(item, index) in categories"
          :key="`category-${item.categoryId}`"
          :to="`${pathPrefix}${item.categoryId}`"
          class="m-0 block h-36px w-full text-center text-24px font-normal leading-36px no-underline focus-visible:outline-unset"
          :class="[selectedIndex === index ? 'text-[#1180FF]' : 'text-[#2D2D2D]']"
        >
          {{ item.categoryName }}
        </RouterLink>

        <div
          class="absolute bottom-0 left-0 h-2px bg-[#1180FF] transition-transform duration-300"
          :style="{
            transform: `translateX(${selectedIndex * 100}%)`,
            width: `${100 / categories.length}%`
          }"
        >
        </div>
        <div class="absolute bottom-0 h-2px w-full bg-[rgba(45,45,45,0.06)]"></div>
      </div>

      <ul class="m-0 list-none p-0" v-if="listing">
        <li class="mt-32px" v-for="item in listing.newsList" :key="item.id">
          <RouterLink :to="`/journalism/detail/${item.id}`" class="group block flex no-underline">
            <div class="h-152px w-272px flex-shrink-0">
              <img class="lazyload block h-full w-full border-hex-979797 rounded-4px object-cover" :data-src="item.cover" :alt="item.title">
            </div>
            <div class="ml-24px flex flex-grow basis-0 flex-col justify-between">
              <h3 class="truncate-ellipsis m-0 truncate text-24px color-hex-2d2d2d font-medium leading-36px group-hover:color-hex-1180FF"><span>{{ item.title }}</span></h3>
              <p class="line-clamp-2 m-0 mt-24px flex-grow text-16px color-hex-9A9EA6 leading-24px">{{ item.intro }}</p>
              <small class="mt-24px block text-right text-14px color-hex-ADB1BA leading-28px">{{ item.releaseTime }}</small>
            </div>
          </RouterLink>
        </li>
      </ul>

      <Pagination class="mt-32px" :pager="pager" :limit="limit" router-mode :set-router-path="setRouterPath" />
    </section>
  </main>
</template>

<style lang="scss" scoped>
.page-journalism {
  .journalism-title {
    background-image: url(@/home/assets/journalism/title-bg.jpg);
  }
}
</style>
